import React, { useEffect, useState } from 'react';
import { FIRELINE_MEDIC_BADGE } from '../brand/firelineMedicBadge';
import { isNativeShell } from '../auth/authRedirect';
import { supabase } from '../auth/supabase';
import { getNativeBiometricStatus, setNativeBiometricEnabled } from '../native/nativeSecureStorage';
import '../auth/member-auth.css';

const EMPTY = { fullName: '', organizationEmail: '', mobile: '', code: '', password: '', confirmPassword: '' };

async function remsRequest(body) {
  const response = await fetch('/api/rems-quick-access', {
    method: 'POST',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload?.error || 'Unable to complete REMS Quick Access.');
    error.code = payload?.code || '';
    throw error;
  }
  return payload;
}

function formatExpiry(value) {
  const date = new Date(value || '');
  return Number.isNaN(date.getTime()) ? '30 days' : date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

function previewBrowserTestMode() {
  if (typeof window === 'undefined') return false;
  const host = String(window.location.hostname || '').toLowerCase();
  return host.endsWith('.vercel.app') && host !== 'sacmetrolinemedic.app';
}

function safeReturnTo() {
  if (typeof window === 'undefined') return '/';
  const value = String(new URLSearchParams(window.location.search).get('returnTo') || '').trim();
  return /^\/handoff\/[A-Za-z0-9_-]+$/i.test(value) ? value : '/';
}

function Brand() {
  return <img className="member-auth-mark member-auth-mark--rems" src={FIRELINE_MEDIC_BADGE} alt="Line Medic" />;
}

export default function RemsQuickAccess() {
  const native = isNativeShell();
  const previewBrowserTest = previewBrowserTestMode();
  const returnTo = safeReturnTo();
  const [form, setForm] = useState(EMPTY);
  const [step, setStep] = useState(native || previewBrowserTest ? 'details' : 'browser');
  const [maskedMobile, setMaskedMobile] = useState('');
  const [profile, setProfile] = useState(null);
  const [biometric, setBiometric] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!native) return;
    let active = true;
    Promise.all([
      supabase.auth.getSession(),
      getNativeBiometricStatus().catch(() => null),
    ]).then(async ([sessionResult, biometricResult]) => {
      if (!active) return;
      if (biometricResult) setBiometric(biometricResult);
      const session = sessionResult?.data?.session;
      if (!session) return;
      const { data } = await supabase.rpc('refresh_member_approval');
      if (!active || !data?.approved) return;
      setProfile(data);
      setStep('existing');
    }).catch(() => {});
    return () => { active = false; };
  }, [native]);

  const update = (key, value) => setForm(current => ({ ...current, [key]: value }));

  const start = async event => {
    event.preventDefault();
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const result = await remsRequest({
        action: 'start',
        fullName: form.fullName,
        organizationEmail: form.organizationEmail,
        mobile: form.mobile,
      });
      setMaskedMobile(result.maskedMobile || 'your mobile number');
      setStep('code');
      setMessage(`Verification code sent to ${result.maskedMobile || 'your mobile number'}.`);
    } catch (startError) {
      setError(startError?.message || 'Unable to send the verification code.');
    } finally {
      setBusy(false);
    }
  };

  const verify = async event => {
    event.preventDefault();
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const result = await remsRequest({
        action: 'verify',
        fullName: form.fullName,
        organizationEmail: form.organizationEmail,
        mobile: form.mobile,
        code: form.code,
      });
      const tokenHash = String(result?.tokenHash || '').trim();
      if (!tokenHash) throw new Error('The REMS device session could not be created.');

      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: result?.verificationType || 'email',
      });
      if (verifyError) throw verifyError;

      const { data: refreshed, error: refreshError } = await supabase.rpc('refresh_member_approval');
      if (refreshError || !refreshed?.approved) throw refreshError || new Error('REMS access could not be activated.');

      setProfile(refreshed);
      setStep('password');
      setMessage('Mobile verified. Create your password to finish setting up your REMS account.');
    } catch (verifyError) {
      setError(verifyError?.message || 'That verification code could not be verified.');
    } finally {
      setBusy(false);
    }
  };

  const setPassword = async event => {
    event.preventDefault();
    setBusy(true);
    setError('');
    setMessage('');
    try {
      if (form.password.length < 8) throw new Error('Use at least 8 characters for your password.');
      if (form.password !== form.confirmPassword) throw new Error('Passwords do not match.');

      const { data: userResult, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      const currentMetadata = userResult?.user?.user_metadata || {};
      const { error: passwordError } = await supabase.auth.updateUser({
        password: form.password,
        data: { ...currentMetadata, enrollment: 'rems_quick_access', rems_password_set: true },
      });
      if (passwordError) throw passwordError;

      setForm(current => ({ ...current, password: '', confirmPassword: '' }));

      if (previewBrowserTest) {
        setStep('complete');
        setMessage('Preview test account created. Continue to REMS to exercise Gear Handoff in the web app.');
        return;
      }

      const status = await getNativeBiometricStatus();
      setBiometric(status);
      setStep('biometric');
      setMessage('Password created. Secure this iPhone with Face ID or Touch ID to continue.');
    } catch (passwordError) {
      setError(passwordError?.message || 'Unable to create your password.');
    } finally {
      setBusy(false);
    }
  };

  const finishBiometric = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await setNativeBiometricEnabled(true);
      setBiometric(result);
      if (!result?.success || !result?.enabled) {
        throw new Error(result?.message || 'Face ID or Touch ID must be enabled to finish REMS Quick Access.');
      }
      setStep('complete');
      setMessage(`${result.label || 'Biometric'} verified. This iPhone is ready for REMS Quick Access.`);
    } catch (biometricError) {
      setError(biometricError?.message || 'Unable to complete biometric setup.');
    } finally {
      setBusy(false);
    }
  };

  if (step === 'browser') {
    return (
      <div className="member-auth-root">
        <section className="member-auth-card">
          <Brand />
          <h1>REMS Quick Access</h1>
          <p>Physical REMS enrollment and Gear Room access require the native Line Medic iPhone app.</p>
          <div className="member-auth-note">If Line Medic is already installed, tap the REMS NFC tag again and choose the app when iOS offers it. Browser access cannot unlock the Gear Room.</div>
        </section>
      </div>
    );
  }

  if (step === 'existing' && profile) {
    const rems = ['rems_temporary', 'rems_permanent'].includes(profile.member_kind);
    return (
      <div className="member-auth-root">
        <section className="member-auth-card">
          <Brand />
          <h1>{rems ? 'REMS Quick Access Active' : 'Line Medic Already Signed In'}</h1>
          <p><strong>{profile.full_name || profile.email}</strong></p>
          {profile.member_kind === 'rems_temporary' && <div className="member-auth-note">You already have a REMS account. Sign in normally with your organization email and password. Temporary access expires {formatExpiry(profile.temporary_access_expires_at)} unless Program Admin verifies you.</div>}
          {profile.member_kind === 'rems_permanent' && <div className="member-auth-note">You already have a permanent REMS account. Sign in normally with your organization email and password.</div>}
          {!rems && <div className="member-auth-note">Your existing Line Medic account already has access to My Gear. REMS Quick Access will not replace it.</div>}
          <button type="button" className="member-auth-primary" onClick={() => window.location.assign(returnTo)}>{returnTo !== '/' ? 'Continue Gear Handoff' : (rems ? 'Continue to REMS' : 'Return to Line Medic')}</button>
        </section>
      </div>
    );
  }

  return (
    <div className="member-auth-root">
      <section className="member-auth-card member-auth-card--rems">
        <Brand />
        <div className="member-auth-rems-kicker">SMFD REMS</div>
        <h1>REMS Quick Access</h1>
        <p className="member-auth-subtitle">For SMFD REMS members who do not already have a Line Medic or REMS account. Verify your identity, create a password, and secure this iPhone.</p>

        {step === 'details' && (
          <form className="member-auth-form member-auth-rems-form" onSubmit={start}>
            <label>First and last name
              <input value={form.fullName} onChange={event => update('fullName', event.target.value)} autoComplete="name" placeholder="First Last" required />
            </label>
            <label>{previewBrowserTest ? 'Organization / test email' : 'District email'}
              <input type="email" value={form.organizationEmail} onChange={event => update('organizationEmail', event.target.value)} autoComplete="email" placeholder={previewBrowserTest ? 'name@metrofire.ca.gov or test@gmail.com' : 'name@metrofire.ca.gov'} required />
            </label>
            <label>Mobile number
              <input type="tel" value={form.mobile} onChange={event => update('mobile', event.target.value)} autoComplete="tel" placeholder="(916) 555-0123" required />
            </label>
            <div className="member-auth-rems-info">
              <strong>30-day temporary access</strong>
              <span>REMS Quick Access is only for people without an existing account. We’ll verify your mobile number by SMS, then you’ll create a password. Program Admin can make your access permanent later.</span>
            </div>
            <button type="submit" className="member-auth-primary" disabled={busy}>{busy ? 'Sending…' : 'Send Verification Code'}</button>
          </form>
        )}

        {step === 'code' && (
          <form className="member-auth-form member-auth-rems-form" onSubmit={verify}>
            <div className="member-auth-rems-step-copy"><strong>Step 1 of 3 · Verify your mobile</strong><br />Enter the verification code sent to <strong>{maskedMobile}</strong>.</div>
            <label>Verification code
              <input inputMode="numeric" autoComplete="one-time-code" value={form.code} onChange={event => update('code', event.target.value)} placeholder="123456" required />
            </label>
            <button type="submit" className="member-auth-primary" disabled={busy}>{busy ? 'Verifying…' : 'Verify Mobile'}</button>
            <button type="button" className="member-auth-link" disabled={busy} onClick={() => { setStep('details'); setForm(current => ({ ...current, code: '' })); setMessage(''); setError(''); }}>Change Information</button>
          </form>
        )}

        {step === 'password' && profile && (
          <form className="member-auth-form member-auth-rems-form" onSubmit={setPassword}>
            <div className="member-auth-rems-step-copy"><strong>Step 2 of 3 · Create your password</strong><br />You’ll use your district email and this password for normal sign-in later.</div>
            <label>New password
              <input type="password" autoComplete="new-password" value={form.password} onChange={event => update('password', event.target.value)} placeholder="At least 8 characters" required />
            </label>
            <label>Confirm password
              <input type="password" autoComplete="new-password" value={form.confirmPassword} onChange={event => update('confirmPassword', event.target.value)} placeholder="Re-enter password" required />
            </label>
            <button type="submit" className="member-auth-primary" disabled={busy}>{busy ? 'Saving…' : 'Create Password & Continue'}</button>
          </form>
        )}

        {step === 'biometric' && profile && (
          <div className="member-auth-rems-step">
            <div className="member-auth-rems-person">{profile.full_name}</div>
            <div className="member-auth-rems-info">
              <strong>Step 3 of 3 · Secure this iPhone</strong>
              <span>Temporary access expires {formatExpiry(profile.temporary_access_expires_at)} unless Program Admin verifies you. Face ID or Touch ID is required for Gear Room access.</span>
            </div>
            {biometric && biometric.available === false && <div className="member-auth-error">Face ID or Touch ID is not available or enrolled on this iPhone. Enable device biometrics before continuing.</div>}
            <button type="button" className="member-auth-primary" disabled={busy || biometric?.available === false} onClick={finishBiometric}>{busy ? 'Checking…' : `Enable ${biometric?.label || 'Face ID / Touch ID'} & Continue`}</button>
          </div>
        )}

        {step === 'complete' && profile && (
          <div className="member-auth-rems-step">
            <div className="member-auth-rems-person">{profile.full_name}</div>
            <div className="member-auth-rems-info member-auth-rems-info--success">
              <strong>REMS Quick Access active</strong>
              <span>{previewBrowserTest ? 'This temporary Preview account is ready for web-app Gear Handoff testing. Production still requires an authorized organization account and native biometric security.' : 'Your individual account is ready on this iPhone. Gear Room unlocks still require a fresh biometric check and current authorization.'}</span>
            </div>
            <button type="button" className="member-auth-primary" onClick={() => window.location.assign(returnTo)}>{returnTo !== '/' ? 'Continue Gear Handoff' : 'Continue to REMS'}</button>
          </div>
        )}

        {message && <div className="member-auth-success member-auth-rems-status">{message}</div>}
        {error && <div className="member-auth-error">{error}</div>}
        {native && !['existing', 'complete'].includes(step) && (
          <button type="button" className="member-auth-link member-auth-rems-back" onClick={() => window.location.assign('/')}>← Back to Line Medic Member Sign In</button>
        )}
      </section>
    </div>
  );
}
